import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Card, CardContent,
  Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert,
  Chip, Divider, Tooltip, Button,
  Tabs, Tab, Collapse, GlobalStyles, ToggleButton, ToggleButtonGroup,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Warning, Error as ErrorIcon, Info,
  Lock, Assignment, Print, Refresh, KeyboardArrowDown, KeyboardArrowUp,
} from '@mui/icons-material';
import { useArquivoAutomatico, TarefaArquivo } from './ArquivoAutomatico';
import { montarCopiaFiel, comoPaginaCompleta } from './arquivarCopia';
import ResumoRGPapel from './ResumoRGPapel';

// ─── Tipos ────────────────────────────────────────────────────────────────────
interface Area { id: number; nome: string; }
interface Ciclo { id: number; nome: string; ativo?: boolean; }
interface PorTipo { R: number; C: number; TB: number; PE: number; O: number; }
interface Agente { nome: string; lancamentos: number; }
interface QrtRow {
  codigo: string; total: number; R: number; C: number; TB: number; PE: number; O: number;
  agentes?: Agente[]; agente_principal?: string;
  data_inicio?: string; data_fim_fad?: string; data_toggle?: string;
}
interface ConcQrt { codigo: string; total_imoveis: number; citacoes: number; status: string; flag: string; }
interface Dashboard {
  area: { id: number; nome: string };
  ciclo: { id: number; nome: string };
  rg: { total_quarteiroes: number; total_imoveis: number; por_tipo: PorTipo; quarteiroes: QrtRow[] };
  campo: {
    registros_fad01: number; por_tipo: PorTipo; trabalhados: number;
    fechados: number; recusados: number; recuperados: number;
    pendencias_finais: number; im_inf: number; qrt_concluidos_declarados: number;
  };
  conciliacao: {
    imoveis_contabilizados: number; diferenca_territorial: number;
    imoveis_em_qrt_nunca_citados: number; residuo_agregado: number;
    cobertura_pct: number; pendencia_final_pct: number;
    por_tipo: { tipo: string; rg: number; campo?: number; inspecionados: number; pendentes: number; diferenca: number }[];
    por_quarteirao: ConcQrt[];
    nunca_citados: string[]; fantasmas: string[];
    sem_declaracao_conclusao: string[];
    toggles_concluidos: number;
  };
  checklist: { errors: string[]; warnings: string[]; info: string[] };
  status: string;
}

// ─── Tipos adicionais Fase 2 ─────────────────────────────────────────────────
interface StatusFechamento {
  fechado: boolean; id?: number; versao: number; status: string | null;
  fechado_por?: string; fechado_em?: string;
  reaberto_por?: string; reaberto_em?: string;
  observacao?: string;
}

// ─── Componente principal ─────────────────────────────────────────────────────
// Ao abrir um boletim pela auditoria, guarda área/ciclo para reabrir a mesma auditoria na volta do FAD-01.
const CHAVE_RETORNO = 'tovis_fechamento_retorno';
function lerRetorno(): { areaId: number; cicloId: number } | null {
  try {
    const v = sessionStorage.getItem(CHAVE_RETORNO);
    if (!v) return null;
    sessionStorage.removeItem(CHAVE_RETORNO);
    const o = JSON.parse(v);
    return typeof o?.areaId === 'number' && typeof o?.cicloId === 'number' ? o : null;
  } catch { return null; }
}

export default function FechamentoArea({ onVoltar, onAbrirFAD01 }: { onVoltar: () => void; onAbrirFAD01?: (id: number) => void }) {
  const [retorno] = useState(lerRetorno);
  const [areas, setAreas] = useState<Area[]>([]);
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [areaId, setAreaId] = useState<number | ''>(retorno?.areaId ?? '');
  const [cicloId, setCicloId] = useState<number | ''>(retorno?.cicloId ?? '');
  const [dados, setDados] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [statusFech, setStatusFech] = useState<StatusFechamento | null>(null);
  const [fechando, setFechando] = useState(false);
  const [dialogo, setDialogo] = useState<'fechar' | 'reabrir' | null>(null);
  const [obsInput, setObsInput] = useState('');
  const [soProblemas, setSoProblemas] = useState(false);
  // Abas: 0 = Auditoria (padrão), 1 = Fechamento
  const [aba, setAba] = useState(0);
  const [auditoria, setAuditoria] = useState<Auditoria | null>(null);
  const [audLoading, setAudLoading] = useState(false);
  const [audErro, setAudErro] = useState('');
  const audSeq = useRef(0);

  // Arquivos: depois de "Fechar Área" dar certo, guarda sozinho 4 documentos (Bloco 8)
  const { arquivar: arquivarAuto, avisoNaTela: avisoArquivo } = useArquivoAutomatico('A área fechou');
  const [arquivarFech, setArquivarFech] = useState<{ ciclo: number; area: number; versao: number | null; auditoria: Promise<string> | null } | null>(null);
  const audLoadingRef = useRef(false);
  audLoadingRef.current = audLoading;
  const fotografarAuditoria = (titulo: string): Promise<string> | null => {
    const partes = Array.from(document.querySelectorAll('.aud-soprint, .aud-card')).filter(el => !el.parentElement?.closest('.aud-card'));
    if (!partes.some(el => el.classList.contains('aud-card'))) return null;
    const p = montarCopiaFiel(partes, titulo); p.catch(() => undefined); return p;
  };
  const tituloFech = (tipo: string, area: number, ciclo: number) =>
    `${tipo} · ${areas.find(a => a.id === area)?.nome ?? `Área ${area}`} · ${ciclos.find(c => c.id === ciclo)?.nome ?? `Ciclo ${ciclo}`}`;

  useEffect(() => {
    fetch('/api/areas/').then(r => r.json()).then((d: any) => {
      const list: Area[] = Array.isArray(d) ? d : d.areas || [];
      setAreas(list.filter((a: Area) => a.nome !== 'ÁreaTeste'));
    });
    fetch('/api/ciclos/').then(r => r.json()).then((d: any) => {
      const list: Ciclo[] = Array.isArray(d) ? d : d.ciclos || [];
      setCiclos(list);
      const ativo = list.find(c => c.ativo);
      if (ativo) setCicloId(prev => prev || ativo.id);
    });
  }, []);

  const carregar = useCallback(async () => {
    if (!areaId || !cicloId) return;
    setLoading(true); setErro(''); setDados(null);
    try {
      const r = await fetch(`/api/fechamento/areas/${areaId}/dashboard?ciclo_id=${cicloId}`);
      if (!r.ok) { setErro(`Erro ${r.status}: ${(await r.json()).detail}`); return; }
      const d = await r.json();
      setDados(d);
      // Carrega status do fechamento
      try {
        const rs = await fetch(`/api/fechamento/areas/${areaId}/status?ciclo_id=${cicloId}`);
        if (rs.ok) setStatusFech(await rs.json());
      } catch { /* ignora */ }
    } catch (e: any) {
      setErro(e.message);
    } finally { setLoading(false); }
  }, [areaId, cicloId]);

  useEffect(() => { if (areaId && cicloId) carregar(); }, [areaId, cicloId, carregar]);

  // ── Auditoria de Fechamento (somente leitura) ──────────────────────────────
  const carregarAuditoria = useCallback(async () => {
    if (!areaId || !cicloId) return;
    const seq = ++audSeq.current;   // descarta resposta atrasada de outra área/ciclo
    setAudLoading(true); setAudErro('');
    try {
      const r = await fetch(`/api/fechamento/areas/${areaId}/auditoria?ciclo_id=${cicloId}`);
      if (!r.ok) {
        let det = '';
        try { const j = await r.json(); det = typeof j.detail === 'string' ? j.detail : ''; } catch { /* sem corpo */ }
        if (seq === audSeq.current) {
          setAuditoria(null);
          setAudErro(`Não foi possível carregar a auditoria (erro ${r.status})${det ? ': ' + det : ''}.`);
        }
        return;
      }
      const d = await r.json();
      if (seq === audSeq.current) setAuditoria(d);
    } catch {
      if (seq === audSeq.current) setAudErro('Não foi possível carregar a auditoria. Verifique a conexão e tente de novo.');
    } finally {
      if (seq === audSeq.current) setAudLoading(false);
    }
  }, [areaId, cicloId]);

  useEffect(() => {
    setAuditoria(null); setAudErro('');
    if (areaId && cicloId) carregarAuditoria();
  }, [areaId, cicloId, carregarAuditoria]);

  // Diálogo de fechar aberto sem auditoria carregada: busca agora (não bloqueia o fechamento)
  useEffect(() => {
    if (dialogo === 'fechar' && !auditoria && !audLoading) carregarAuditoria();
  }, [dialogo]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirBoletim = onAbrirFAD01 ? (id: number) => {
    try {
      if (areaId && cicloId) sessionStorage.setItem(CHAVE_RETORNO, JSON.stringify({ areaId, cicloId }));
    } catch { /* sem armazenamento: volta sem área selecionada */ }
    onAbrirFAD01(id);
  } : undefined;

  const executarFechar = async () => {
    if (!areaId || !cicloId) return;
    setFechando(true);
    try {
      const fotoAuditoria = fotografarAuditoria(tituloFech('Auditoria', Number(areaId), Number(cicloId))); // fora da aba Auditoria = null → pisca e volta depois
      const r = await fetch(`/api/fechamento/areas/${areaId}/fechar?ciclo_id=${cicloId}`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ fechado_por: 'supervisor', observacao: obsInput }),
      });
      if (!r.ok) { alert(`Erro: ${(await r.json()).detail}`); return; }
      const res = await r.json();
      setStatusFech({ fechado: true, versao: res.versao, status: 'FECHADO', fechado_por: 'supervisor', fechado_em: res.fechado_em });
      setDialogo(null); setObsInput('');
      alert(`✅ Área fechada! Versão ${res.versao} — RG: ${res.rg_total_imoveis} imóveis, IM.INF.: ${res.im_inf}, Diferença: ${res.diferenca_territorial > 0 ? '+' : ''}${res.diferenca_territorial}`);
      setArquivarFech({ ciclo: Number(cicloId), area: Number(areaId), versao: res.versao ?? null, auditoria: fotoAuditoria });
    } finally { setFechando(false); }
  };

  const executarReabrir = async () => {
    if (!areaId || !cicloId) return;
    setFechando(true);
    try {
      const r = await fetch(`/api/fechamento/areas/${areaId}/reabrir?ciclo_id=${cicloId}`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ reaberto_por: 'supervisor', motivo: obsInput }),
      });
      if (!r.ok) { alert(`Erro: ${(await r.json()).detail}`); return; }
      setStatusFech(prev => prev ? { ...prev, fechado: false, status: 'REABERTO' } : null);
      setDialogo(null); setObsInput('');
    } finally { setFechando(false); }
  };

  // ── Geração de documentos imprimíveis (Fase 3) ─────────────────────────────
  const montarDocumentoHtml = (tipo: 'termo' | 'resumo' | 'conciliacao'): string | null => {
    if (!dados) return null;
    const hoje = new Date().toLocaleDateString('pt-BR');
    const d = dados;
    const CSS = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; padding: 24px; }
      h1 { font-size: 17px; margin-bottom: 2px; color: #0F2347; }
      h2 { font-size: 13px; margin: 14px 0 6px; color: #1B3A6B; border-bottom: 2px solid #1B3A6B; padding-bottom: 3px; }
      .sub { font-size: 11px; color: #555; margin-bottom: 4px; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #0F2347; padding-bottom: 10px; margin-bottom: 14px; }
      .cab .org { text-align: right; font-size: 10px; color: #555; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
      th { background: #0F2347; color: #fff; padding: 5px 7px; font-size: 9.5px; text-transform: uppercase; text-align: center; border: 1px solid #0F2347; }
      th.l, td.l { text-align: left; }
      td { padding: 4px 7px; border: 1px solid #CBD5E1; text-align: center; font-variant-numeric: tabular-nums; }
      tr:nth-child(even) td { background: #F1F5F9; }
      tr.total td { background: #1B3A6B !important; color: #fff; font-weight: bold; }
      tr.subtotal td { background: #FFFBEB; color: #92400E; font-weight: bold; border-top: 2px solid #F59E0B; border-bottom: 2px solid #F59E0B; }
      tr.erro td { background: #FEE2E2; color: #991B1B; }
      tr.alerta td { background: #FEF3C7; color: #92400E; }
      .box { border: 1px solid #CBD5E1; border-radius: 6px; padding: 10px 12px; margin-bottom: 10px; }
      .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      .metric { display: inline-block; margin-right: 22px; }
      .metric .v { font-size: 20px; font-weight: bold; color: #0F2347; }
      .metric .l { font-size: 9px; text-transform: uppercase; color: #666; }
      .erro-item { color: #991B1B; font-weight: bold; }
      .alerta-item { color: #92400E; }
      .assinatura { margin-top: 40px; display: flex; justify-content: space-around; text-align: center; }
      .assinatura .linha { border-top: 1px solid #111; width: 220px; padding-top: 4px; font-size: 10px; }
      .status-tag { display: inline-block; padding: 3px 12px; border-radius: 12px; font-weight: bold; font-size: 11px; }
      .rodape { margin-top: 18px; font-size: 9px; color: #888; text-align: center; border-top: 1px solid #ddd; padding-top: 6px; }
      @media print { body { padding: 12px; } h2 { page-break-after: avoid; } tr { page-break-inside: avoid; } }
    `;
    const cabecalho = `
      <div class="cab">
        <div>
          <h1 style="font-size:20px;color:#0F2347">TOVIS</h1>
          <div class="sub">Sistema de Gestão de Endemias</div>
        </div>
        <div class="org">
          Emitido em ${hoje}<br/>tovis.com.br
        </div>
      </div>`;

    let corpo = '';

    if (tipo === 'termo') {
      const st = d.status === 'PRONTO_PARA_FECHAMENTO'
        ? '<span class="status-tag" style="background:#D1FAE5;color:#065F46">PRONTO PARA FECHAMENTO</span>'
        : '<span class="status-tag" style="background:#FEE2E2;color:#991B1B">FECHADO COM PENDÊNCIAS</span>';
      corpo = `
        <h1 style="text-align:center;margin:6px 0 14px">TERMO DE FECHAMENTO DE ÁREA</h1>
        <div class="box">
          <div class="grid2">
            <div><b>Área:</b> ${d.area.nome}</div>
            <div><b>Ciclo:</b> ${d.ciclo.nome}</div>
            <div><b>Data do fechamento:</b> ${hoje}</div>
            <div><b>Situação:</b> ${st}</div>
          </div>
        </div>
        <h2>Consolidado Territorial</h2>
        <div class="box">
          <span class="metric"><span class="v">${d.rg.total_quarteiroes}</span><br/><span class="l">Quarteirões (RG)</span></span>
          <span class="metric"><span class="v">${d.rg.total_imoveis.toLocaleString('pt-BR')}</span><br/><span class="l">Imóveis cadastrados</span></span>
          <span class="metric"><span class="v">${d.campo.im_inf.toLocaleString('pt-BR')}</span><br/><span class="l">Imóveis informados (campo)</span></span>
          <span class="metric"><span class="v">${d.conciliacao.cobertura_pct}%</span><br/><span class="l">Cobertura efetiva</span></span>
        </div>
        <h2>Situação dos Imóveis (Campo)</h2>
        <div class="box">
          <span class="metric"><span class="v">${d.campo.trabalhados.toLocaleString('pt-BR')}</span><br/><span class="l">Trabalhados</span></span>
          <span class="metric"><span class="v">${d.campo.fechados}</span><br/><span class="l">Fechados</span></span>
          <span class="metric"><span class="v">${d.campo.recusados}</span><br/><span class="l">Recusas</span></span>
          <span class="metric"><span class="v">${d.campo.recuperados}</span><br/><span class="l">Recuperados</span></span>
        </div>
        ${d.checklist.errors.length > 0 ? `
          <h2>Pendências Registradas</h2>
          <div class="box">
            ${d.checklist.errors.map(e => `<div class="erro-item">• ${e}</div>`).join('')}
          </div>` : '<div class="box" style="color:#065F46;font-weight:bold">✓ Nenhuma pendência crítica — área coberta integralmente.</div>'}
        <div class="assinatura">
          <div class="linha">Supervisor de Endemias</div>
          <div class="linha">Coordenação Municipal</div>
        </div>`;
    }

    if (tipo === 'resumo') {
      const SUBTOTAL_A_CADA = 22;
      const linhas = (() => {
        const rows: string[] = [];
        let acc: Record<string,number> = { R:0, C:0, TB:0, PE:0, O:0, total:0 };
        d.rg.quarteiroes.forEach((q, idx) => {
          const fl = d.conciliacao.por_quarteirao.find(c => c.codigo === q.codigo)?.flag || 'ok';
          const cls = fl === 'erro' ? 'erro' : fl === 'alerta' ? 'alerta' : '';
          rows.push(`<tr class="${cls}">
            <td class="l">${q.codigo}${fl === 'erro' ? ' ⚠' : ''}</td>
            <td>${q.R || '—'}</td><td>${q.C || '—'}</td><td>${q.TB || '—'}</td>
            <td>${q.PE || '—'}</td><td>${q.O || '—'}</td>
            <td><b>${q.total}</b></td>
            <td class="l">${q.agente_principal || '—'}</td>
          </tr>`);
          acc.R += q.R || 0; acc.C += q.C || 0; acc.TB += q.TB || 0;
          acc.PE += q.PE || 0; acc.O += q.O || 0; acc.total += q.total || 0;
          const pos = idx + 1;
          const isLast = pos === d.rg.quarteiroes.length;
          if (pos % SUBTOTAL_A_CADA === 0 && !isLast) {
            rows.push(`<tr class="subtotal">
              <td class="l">SUBTOTAL ACUMULADO (1–${pos})</td>
              <td>${acc.R || '—'}</td><td>${acc.C || '—'}</td><td>${acc.TB || '—'}</td>
              <td>${acc.PE || '—'}</td><td>${acc.O || '—'}</td>
              <td><b>${acc.total}</b></td><td></td>
            </tr>`);
          }
        });
        return rows.join('');
      })();
      corpo = `
        <h1 style="text-align:center;margin:6px 0 4px">RESUMO DO RECONHECIMENTO GEOGRÁFICO</h1>
        <div class="sub" style="text-align:center;margin-bottom:12px">${d.area.nome} · ${d.ciclo.nome} · FA-D-06</div>
        <table>
          <thead><tr>
            <th class="l">Nº Quarteirão</th><th>Resid.</th><th>Comerc.</th><th>T. Baldio</th>
            <th>P. Estrat.</th><th>Outro</th><th>Total</th><th class="l">Agente</th>
          </tr></thead>
          <tbody>
            ${linhas}
            <tr class="total">
              <td class="l">TOTAL</td>
              <td>${d.rg.por_tipo.R}</td><td>${d.rg.por_tipo.C}</td><td>${d.rg.por_tipo.TB}</td>
              <td>${d.rg.por_tipo.PE}</td><td>${d.rg.por_tipo.O}</td>
              <td>${d.rg.total_imoveis}</td><td></td>
            </tr>
          </tbody>
        </table>
        <div class="assinatura">
          <div class="linha">Responsável pelo preenchimento</div>
          <div class="linha">Data</div>
        </div>`;
    }

    if (tipo === 'conciliacao') {
      const tipoRows = d.conciliacao.por_tipo.map(t => `
        <tr><td class="l">${t.tipo === 'R' ? 'Residência' : t.tipo === 'C' ? 'Comércio' : t.tipo === 'TB' ? 'Terreno Baldio' : t.tipo === 'PE' ? 'Ponto Estratégico' : 'Outros'}</td>
        <td>${t.rg}</td><td>${t.inspecionados ?? 0}</td>
        <td style="color:${(t.pendentes ?? 0) === 0 ? '#065F46' : '#92400E'};font-weight:bold">${(t.pendentes ?? 0) > 0 ? (t.pendentes ?? 0) : '—'}</td></tr>`).join('');
      corpo = `
        <h1 style="text-align:center;margin:6px 0 4px">RELATÓRIO DE COBERTURA POR TIPO DE IMÓVEL</h1>
        <div class="sub" style="text-align:center;margin-bottom:12px">${d.area.nome} · ${d.ciclo.nome}</div>
        <h2>Comparativo por Tipo de Imóvel</h2>
        <table>
          <thead><tr><th class="l">Tipo</th><th>Cadastrado (RG)</th><th>Inspecionados</th><th>Pendentes</th></tr></thead>
          <tbody>
            ${tipoRows}
            <tr class="total"><td class="l">TOTAL</td><td>${d.rg.total_imoveis}</td><td>${d.conciliacao.por_tipo.reduce((s, t) => s + (t.inspecionados ?? 0), 0)}</td><td>${d.conciliacao.por_tipo.reduce((s, t) => s + (t.pendentes ?? 0), 0)}</td></tr>
          </tbody>
        </table>
        <h2>Verificação de Auditoria</h2>
        <div class="box">
          ${d.checklist.errors.map(e => `<div class="erro-item">🔴 ${e}</div>`).join('')}
          ${d.checklist.warnings.map(w => `<div class="alerta-item">🟡 ${w}</div>`).join('')}
          ${d.checklist.info.map(i => `<div style="color:#555">ℹ️ ${i}</div>`).join('')}
        </div>
        ${d.conciliacao.nunca_citados.length > 0 ? `
          <h2>Quarteirões Nunca Citados em FAD-01</h2>
          <div class="box erro-item">${d.conciliacao.nunca_citados.map(c => 'Q' + c).join(', ')} — somam ${d.conciliacao.imoveis_em_qrt_nunca_citados} imóvel(is)</div>` : ''}
        <div class="assinatura">
          <div class="linha">Supervisor de Endemias</div>
          <div class="linha">Data</div>
        </div>`;
    }

    const titulos = { termo: 'Termo de Fechamento', resumo: 'Resumo RG (FA-D-06)', conciliacao: 'Relatório de Conciliação' };
    return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
      <title>${titulos[tipo]} — ${d.area.nome}</title><style>${CSS}</style></head>
      <body>${cabecalho}${corpo}<div class="rodape">TOVIS · ${d.area.nome} · ${d.ciclo.nome} · ${hoje}</div></body></html>`;
  };

  const gerarDocumento = (tipo: 'termo' | 'resumo' | 'conciliacao') => {
    const html = montarDocumentoHtml(tipo);
    if (!html) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  // Arquivamento automático depois de fechar: passa pela aba Auditoria, tira a cópia e volta (Walber, 01/10/2026)
  useEffect(() => {
    if (!arquivarFech) return;
    const alvo = arquivarFech;
    setArquivarFech(null);
    const tAud = tituloFech('Auditoria', alvo.area, alvo.ciclo);
    void (async () => {
      let foto: Promise<string> | null = alvo.auditoria;
      let erroAud: Error | null = null;
      if (!foto) {
        setAba(0);
        try {
          const inicio = Date.now();
          while (audLoadingRef.current || !document.querySelector('.aud-card')) {
            if (Date.now() - inicio >= 15000) throw new Error('A Auditoria não carregou a tempo. Abra a aba Auditoria e clique em Tentar de novo.');
            await new Promise(ok => setTimeout(ok, 200));
          }
          foto = fotografarAuditoria(tAud);
          if (!foto) throw new Error('A Auditoria não estava aberta na tela. Abra a Auditoria da área e clique em Tentar de novo.');
        } catch (e) {
          erroAud = e instanceof Error ? e : new Error(String(e));
        } finally {
          setAba(1);
        }
      }
      const ref = alvo.versao ? `Fechamento versão ${alvo.versao}` : 'Fechamento';
      const obs = `Arquivado automaticamente ao fechar a área (fechamento versão ${alvo.versao ?? '?'}).`;
      const base = { pasta: 'fechamento_area' as const, ciclo_id: alvo.ciclo, area_id: alvo.area, referencia: ref, avisos: [] as string[], origem: 'automatico' as const, observacao: obs };
      const doc = (tipo: 'termo' | 'resumo' | 'conciliacao', nome: string): TarefaArquivo => ({
        pedido: { ...base, tipo: `fa_${tipo}`, chave: `fa_${tipo}|c${alvo.ciclo}|a${alvo.area}`, titulo: tituloFech(nome, alvo.area, alvo.ciclo) },
        html: () => {
          const h = montarDocumentoHtml(tipo);
          if (!h) throw new Error('Os dados da área não estavam carregados. Abra a área e clique em Tentar de novo.');
          return comoPaginaCompleta(h);
        },
      });
      await arquivarAuto([
        {
          pedido: { ...base, tipo: 'fa_auditoria', chave: `fa_auditoria|c${alvo.ciclo}|a${alvo.area}`, titulo: tAud },
          html: async () => {
            if (foto) { const f = foto; foto = null; try { return await f; } catch { /* tenta pela tela */ } }
            else if (erroAud) { const e = erroAud; erroAud = null; throw e; }
            const p = fotografarAuditoria(tAud);
            if (!p) throw new Error('A Auditoria não estava aberta na tela. Abra a Auditoria da área e clique em Tentar de novo.');
            return p;
          },
        },
        doc('termo', 'Termo de Fechamento'),
        doc('resumo', 'Resumo RG (FA-D-06)'),
        doc('conciliacao', 'Relatório de Conciliação'),
      ]);
    })();
  }, [arquivarFech]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cabeçalho azul escuro ──────────────────────────────────────────────────
  const TH: React.CSSProperties = {
    background: '#0a1830', color: '#DCE6FB', padding: '8px 10px',
    fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.5px', textAlign: 'center', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 2,
    borderRight: '2px solid #1e3a5f',
  };
  const TD: React.CSSProperties = {
    padding: '5px 10px', fontSize: '0.82rem', textAlign: 'center',
    borderBottom: '1px solid #E5E7EB', fontVariantNumeric: 'tabular-nums',
    borderRight: '2px solid #D8E0EE',
  };
  const TDL: React.CSSProperties = { ...TD, textAlign: 'left', fontWeight: 700, borderRight: '2px solid #D8E0EE' };

  const corDif = (d: number) =>
    d === 0 ? '#065F46' : d > 0 ? '#0277BD' : '#991B1B';

  const statusColor = dados?.status === 'PRONTO_PARA_FECHAMENTO'
    ? { bg: '#D1FAE5', border: '#A7F3D0', text: '#065F46' }
    : { bg: '#FEE2E2', border: '#FCA5A5', text: '#991B1B' };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F0F4FF' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B)' }} className="no-print">
        <Toolbar>
          <IconButton color="inherit" onClick={onVoltar} sx={{ mr: 1 }}><ArrowBack /></IconButton>
          <Lock sx={{ mr: 1 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', flexGrow: 1 }}>Fechamento de Área</Typography>
          {dados && (
            <Chip label={dados.status === 'PRONTO_PARA_FECHAMENTO' ? '✓ Pronto para fechar' : '⚠ Com pendências'}
              size="small"
              sx={{ bgcolor: statusColor.bg, color: statusColor.text, fontWeight: 800, border: `1px solid ${statusColor.border}`, fontSize: '0.72rem' }} />
          )}
          {dados && (
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }} className="no-print">
              <Tooltip title="Termo de Fechamento">
                <IconButton color="inherit" size="small" onClick={() => gerarDocumento('termo')}>
                  <Assignment sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Resumo RG (FA-D-06)">
                <IconButton color="inherit" size="small" onClick={() => gerarDocumento('resumo')}>
                  <Print sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Relatório de Conciliação">
                <IconButton color="inherit" size="small" onClick={() => gerarDocumento('conciliacao')}>
                  <Assignment sx={{ fontSize: 20, opacity: 0.7 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Seletores */}
        <Card className="aud-noprint" sx={{ borderRadius: 3, border: '1px solid #BFDBFE' }}>
          <Box sx={{ background: '#1B3A6B', px: 2.5, py: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#fff' }}>
              Selecionar Área e Ciclo
            </Typography>
          </Box>
          <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', py: '12px !important' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Área</InputLabel>
              <Select value={areaId} label="Área" onChange={e => setAreaId(e.target.value as number)}>
                {areas.map(a => <MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Ciclo</InputLabel>
              <Select value={cicloId} label="Ciclo" onChange={e => setCicloId(e.target.value as number)}>
                {ciclos.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
              </Select>
            </FormControl>
            {loading && <CircularProgress size={24} sx={{ mt: 1 }} />}
          </CardContent>
        </Card>

        {/* Abas: 1 · Auditoria (padrão) | 2 · Fechamento */}
        <Card className="aud-noprint" sx={{ borderRadius: 3, border: '1px solid #BFDBFE' }}>
          <Tabs value={aba} onChange={(_, v) => setAba(v)} variant="fullWidth">
            <Tab label="1 · Auditoria" sx={{ fontWeight: 800 }} />
            <Tab label="2 · Fechamento" sx={{ fontWeight: 800 }} />
          </Tabs>
        </Card>

        {aba === 0 && (
          <PainelAuditoria aud={auditoria} loading={audLoading} erro={audErro}
            selecionado={!!areaId && !!cicloId}
            onAtualizar={carregarAuditoria} onAbrirFAD01={abrirBoletim} />
        )}

        {aba === 1 && <>
        {erro && <Alert severity="error">{erro}</Alert>}

        {/* Banner de status do fechamento */}
        {statusFech?.status && (
          <Card sx={{ borderRadius: 2, border: statusFech.fechado ? '2px solid #065F46' : '2px solid #92400E', overflow: 'hidden' }} className="no-print">
            <CardContent sx={{ py: '10px !important', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1 }}>
                {statusFech.fechado ? (
                  <>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#065F46' }}>
                      🔒 Área FECHADA — Versão {statusFech.versao}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#374151' }}>
                      Fechado por {statusFech.fechado_por} em {statusFech.fechado_em ? new Date(statusFech.fechado_em).toLocaleString('pt-BR') : '—'}
                      {statusFech.observacao ? ` · ${statusFech.observacao}` : ''}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#92400E' }}>
                    🔓 Área REABERTA (versão {statusFech.versao}) — dados atuais podem divergir do snapshot
                  </Typography>
                )}
              </Box>
              {statusFech.fechado ? (
                <Button variant="outlined" color="warning" size="small"
                  sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                  onClick={() => { setObsInput(''); setDialogo('reabrir'); }}>
                  🔓 Reabrir
                </Button>
              ) : (
                <Button variant="contained" size="small"
                  disabled={!dados}
                  sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#065F46', '&:hover': { bgcolor: '#034732' } }}
                  onClick={() => { setObsInput(''); setDialogo('fechar'); }}>
                  🔒 Fechar novamente
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Botão Fechar Área (só aparece quando dados carregados e nunca fechado) */}
        {dados && !statusFech?.fechado && !statusFech?.id && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} className="no-print">
            <Button variant="contained" size="large"
              sx={{ fontWeight: 800, bgcolor: '#065F46', '&:hover': { bgcolor: '#034732' }, px: 4, borderRadius: 2 }}
              onClick={() => { setObsInput(''); setDialogo('fechar'); }}>
              🔒 Fechar Área
            </Button>
          </Box>
        )}

        {/* Diálogo de confirmação (fechar ou reabrir) */}
        {dialogo && (
          <Card sx={{ borderRadius: 3, border: `2px solid ${dialogo === 'fechar' ? '#065F46' : '#92400E'}`, p: 2 }} className="no-print">
            {dialogo === 'fechar' && !auditoria && audLoading && (
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1 }}>Conferindo a auditoria…</Typography>
            )}
            {dialogo === 'fechar' && auditoria && auditoria.semaforo !== 'verde' && (() => {
              const nV = auditoria.achados.filter(a => a.nivel === 'vermelho').length;
              const nA = auditoria.achados.filter(a => a.nivel === 'amarelo').length;
              const primeiros = auditoria.achados.filter(a => a.nivel === 'vermelho' || a.nivel === 'amarelo').slice(0, 3);
              return (
                <Alert severity={auditoria.semaforo === 'vermelho' ? 'error' : 'warning'} sx={{ mb: 1.5 }}
                  action={<Button color="inherit" size="small" sx={{ fontWeight: 800 }} onClick={() => setAba(0)}>Ver auditoria</Button>}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                    A auditoria encontrou {nV} {nV === 1 ? 'achado vermelho' : 'achados vermelhos'} e {nA} {nA === 1 ? 'amarelo' : 'amarelos'}.
                  </Typography>
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {primeiros.map((a, i) => (
                      <li key={i}><Typography sx={{ fontSize: '0.78rem' }}>{a.titulo}</Typography></li>
                    ))}
                  </Box>
                </Alert>
              );
            })()}
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1, color: dialogo === 'fechar' ? '#065F46' : '#92400E' }}>
              {dialogo === 'fechar' ? '🔒 Confirmar fechamento da área' : '🔓 Confirmar reabertura da área'}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', mb: 1.5, color: '#374151' }}>
              {dialogo === 'fechar'
                ? `Isso gravará um snapshot dos dados atuais. A conciliação será congelada neste momento. Versão ${(statusFech?.versao ?? 0) + 1}.`
                : 'A área voltará ao estado REABERTO. Os dados atuais continuarão sendo editáveis.'}
            </Typography>
            <Box component="input" placeholder="Observação (opcional)"
              value={obsInput} onChange={(e: any) => setObsInput(e.target.value)}
              sx={{ width: '100%', mb: 1.5, p: 1, borderRadius: 1, border: '1px solid #D1D5DB', fontSize: '0.82rem', fontFamily: 'inherit' }} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button variant="outlined" size="small" onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button variant="contained" size="small" disabled={fechando}
                sx={{ fontWeight: 700, bgcolor: dialogo === 'fechar' ? '#065F46' : '#92400E',
                  '&:hover': { bgcolor: dialogo === 'fechar' ? '#034732' : '#7F1D1D' } }}
                onClick={dialogo === 'fechar' ? executarFechar : executarReabrir}>
                {fechando ? 'Aguarde...' : dialogo === 'fechar' ? '✅ Confirmar Fechamento' : '✅ Confirmar Reabertura'}
              </Button>
            </Box>
          </Card>
        )}

        {dados && <>

          {/* ── BLOCO 1: CHECKLIST ─────────────────────────────────────── */}
          <Card sx={{ borderRadius: 3, border: `2px solid ${statusColor.border}`, overflow: 'hidden' }}>
            <Box sx={{ background: dados.status === 'PRONTO_PARA_FECHAMENTO' ? '#065F46' : '#991B1B', px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              {dados.status === 'PRONTO_PARA_FECHAMENTO'
                ? <CheckCircle sx={{ color: '#fff', fontSize: 18 }} />
                : <Warning sx={{ color: '#fff', fontSize: 18 }} />}
              <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#fff' }}>
                Checklist de Fechamento — {dados.area.nome} / {dados.ciclo.nome}
              </Typography>
            </Box>
            <CardContent sx={{ py: '12px !important', display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {dados.checklist.errors.map((e, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: '#FEE2E2', p: 1, borderRadius: 1.5 }}>
                  <ErrorIcon sx={{ color: '#991B1B', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#7F1D1D' }}>{e}</Typography>
                </Box>
              ))}
              {dados.checklist.warnings.map((w, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: '#FEF3C7', p: 1, borderRadius: 1.5 }}>
                  <Warning sx={{ color: '#92400E', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#78350F' }}>{w}</Typography>
                </Box>
              ))}
              {dados.checklist.info.map((inf, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: '#EFF6FF', p: 1, borderRadius: 1.5 }}>
                  <Info sx={{ color: '#1B3A6B', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#1e3a5f' }}>{inf}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* ── BLOCO 2: MÉTRICAS LADO A LADO ─────────────────────────── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>

            {/* RG */}
            <Card sx={{ borderRadius: 3, border: '1px solid #BFDBFE', overflow: 'hidden' }}>
              <Box sx={{ background: '#0a1830', px: 2.5, py: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#DCE6FB' }}>
                  Base RG — Cadastro Territorial
                </Typography>
              </Box>
              <CardContent sx={{ py: '12px !important' }}>
                <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#0F2347', fontVariantNumeric: 'tabular-nums' }}>{dados.rg.total_quarteiroes}</Typography>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#6B7280' }}>Quarteirões</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#0F2347', fontVariantNumeric: 'tabular-nums' }}>{dados.rg.total_imoveis.toLocaleString('pt-BR')}</Typography>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#6B7280' }}>Imóveis no RG</Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {(['R','C','TB','PE','O'] as const).map(t => (
                    <Box key={t} sx={{ textAlign: 'center', minWidth: 44, p: 0.5, borderRadius: 1, bgcolor: '#F0F4FF' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1B3A6B', fontVariantNumeric: 'tabular-nums' }}>{dados.rg.por_tipo[t]}</Typography>
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#6B7280' }}>{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Campo */}
            <Card sx={{ borderRadius: 3, border: '1px solid #BFDBFE', overflow: 'hidden' }}>
              <Box sx={{ background: '#0a1830', px: 2.5, py: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#DCE6FB' }}>
                  Campo — Execução no Ciclo
                </Typography>
              </Box>
              <CardContent sx={{ py: '12px !important' }}>
                <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
                  {[
                    { v: dados.campo.trabalhados, l: 'Trabalhados', c: '#065F46' },
                    { v: dados.campo.fechados, l: 'Fechados', c: '#92400E' },
                    { v: dados.campo.recusados, l: 'Recusas', c: '#991B1B' },
                    { v: dados.campo.recuperados, l: 'Recuperados', c: '#0277BD' },
                    { v: dados.campo.pendencias_finais, l: 'Pend. Final', c: '#92400E' },
                    { v: dados.campo.im_inf, l: 'IM.INF.', c: '#0F2347' },
                  ].map(({ v, l, c }) => (
                    <Box key={l} sx={{ textAlign: 'center', minWidth: 60 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: c, fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString('pt-BR')}</Typography>
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', color: '#6B7280' }}>{l}</Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: 600 }}>Cobertura efetiva</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: dados.conciliacao.cobertura_pct >= 95 ? '#065F46' : '#991B1B' }}>
                      {dados.conciliacao.cobertura_pct}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: 600 }}>Pendência final</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#92400E' }}>{dados.conciliacao.pendencia_final_pct}%</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: 600 }}>FAD-01 lançados</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0F2347' }}>{dados.campo.registros_fad01}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* ── BLOCO 3: CONCILIAÇÃO TERRITORIAL ─────────────────────── */}
          <Card sx={{ borderRadius: 3, border: '1px solid #BFDBFE', overflow: 'hidden' }}>
            <Box sx={{ background: '#1B3A6B', px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Assignment sx={{ color: '#fff', fontSize: 18 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#fff', flex: 1 }}>
                Cobertura por Tipo de Imóvel
              </Typography>
              <Chip
                label={`Cobertura: ${dados.conciliacao.cobertura_pct}%`}
                size="small"
                sx={{ bgcolor: dados.conciliacao.diferenca_territorial === 0 ? '#D1FAE5' : '#FEE2E2', color: dados.conciliacao.diferenca_territorial === 0 ? '#065F46' : '#991B1B', fontWeight: 800, fontSize: '0.7rem' }} />
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  <th style={{ ...TH, textAlign: 'left' }}>Tipo</th>
                  <th style={TH}>RG</th>
                  <th style={TH}>Inspecionados</th>
                  <th style={TH}>Pendentes</th>
                </tr></thead>
                <tbody>
                  {dados.conciliacao.por_tipo.map((t, i) => (
                    <tr key={t.tipo} style={{ background: i % 2 === 0 ? '#fff' : '#F9FAFF' }}>
                      <td style={TDL}>{t.tipo === 'R' ? 'Residência' : t.tipo === 'C' ? 'Comércio' : t.tipo === 'TB' ? 'Terreno Baldio' : t.tipo === 'PE' ? 'Ponto Estrat.' : 'Outros'}</td>
                      <td style={TD}>{t.rg.toLocaleString('pt-BR')}</td>
                      <td style={TD}>{(t.inspecionados ?? 0).toLocaleString('pt-BR')}</td>
                      <td style={{ ...TD, fontWeight: 700, color: (t.pendentes ?? 0) > 0 ? '#92400E' : '#065F46' }}>{(t.pendentes ?? 0) > 0 ? (t.pendentes ?? 0).toLocaleString('pt-BR') : '—'}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#EFF6FF' }}>
                    <td style={{ ...TDL, color: '#0F2347' }}>TOTAL</td>
                    <td style={{ ...TD, fontWeight: 800 }}>{dados.rg.total_imoveis.toLocaleString('pt-BR')}</td>
                    <td style={{ ...TD, fontWeight: 800 }}>{dados.conciliacao.por_tipo.reduce((s, t) => s + (t.inspecionados ?? 0), 0).toLocaleString('pt-BR')}</td>
                    <td style={{ ...TD, fontWeight: 800, color: '#92400E' }}>{dados.conciliacao.por_tipo.reduce((s, t) => s + (t.pendentes ?? 0), 0).toLocaleString('pt-BR')}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </Card>

          {/* ── BLOCO 4: RESUMO RG (FA-D-06) — modo "passar para o papel" (ResumoRGPapel.tsx, 01/10/2026) ── */}
          <ResumoRGPapel
            quarteiroes={dados.rg.quarteiroes}
            conciliacao={dados.conciliacao.por_quarteirao}
            totalImoveisRG={dados.rg.total_imoveis}
            totalQuarteiroesRG={dados.rg.total_quarteiroes}
            areaId={areaId}
            cicloId={cicloId}
          />

          {/* ── BLOCO 5: STATUS POR QUARTEIRÃO ───────────────────────── */}
          {(dados.conciliacao.nunca_citados.length > 0 || dados.conciliacao.sem_declaracao_conclusao.length > 0 || dados.conciliacao.fantasmas.length > 0) && (
            <Card sx={{ borderRadius: 3, border: '2px solid #FCA5A5', overflow: 'hidden' }}>
              <Box sx={{ background: '#991B1B', px: 2.5, py: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#fff' }}>
                  Atenção — Quarteirões com Problema
                </Typography>
              </Box>
              <CardContent sx={{ py: '12px !important', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {dados.conciliacao.nunca_citados.length > 0 && (
                  <Alert severity="error" icon={<ErrorIcon />}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Nunca citados em FAD-01 ({dados.conciliacao.nunca_citados.length})</Typography>
                    <Typography sx={{ fontSize: '0.78rem', mt: 0.3 }}>
                      {dados.conciliacao.nunca_citados.map(c => `Q${c}`).join(', ')} — somam {dados.conciliacao.imoveis_em_qrt_nunca_citados} imóvel(is)
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', mt: 0.5, color: '#7F1D1D' }}>
                      Ação: verificar se foram trabalhados em campo mas sem registro. Se não trabalhados, retornar agente antes de fechar.
                    </Typography>
                  </Alert>
                )}
                {dados.conciliacao.sem_declaracao_conclusao.length > 0 && (
                  <Alert severity="warning" icon={<Warning />}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Trabalhados sem declaração de conclusão ({dados.conciliacao.sem_declaracao_conclusao.length})</Typography>
                    <Typography sx={{ fontSize: '0.78rem', mt: 0.3 }}>
                      {dados.conciliacao.sem_declaracao_conclusao.map(c => `Q${c}`).join(', ')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', mt: 0.5, color: '#78350F' }}>
                      Ação: conferir boletim físico. Se concluído, corrigir o campo "Nº Quart. Concluídos" no FAD-01 correspondente.
                    </Typography>
                  </Alert>
                )}
                {dados.conciliacao.fantasmas.length > 0 && (
                  <Alert severity="warning" icon={<Warning />}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Lançados em FAD-01 mas não cadastrados no RG ({dados.conciliacao.fantasmas.length})</Typography>
                    <Typography sx={{ fontSize: '0.78rem', mt: 0.3 }}>
                      {dados.conciliacao.fantasmas.map(c => `Q${c}`).join(', ')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', mt: 0.5, color: '#78350F' }}>
                      Ação: verificar se o código foi digitado incorretamente ou se o quarteirão precisa ser cadastrado no RG.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

        </>}

        {!dados && !loading && areaId && cicloId && (
          <Box sx={{ textAlign: 'center', py: 6, color: '#6B7280' }}>
            <Typography sx={{ fontWeight: 600 }}>Selecione uma área e um ciclo para carregar a conciliação</Typography>
          </Box>
        )}
        </>}

      </Box>
      {avisoArquivo}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Aba 1 — Auditoria de Fechamento (GET /api/fechamento/areas/{area}/auditoria)
// Somente leitura. Cores só do tema MUI (error / warning / success / info).
// ═══════════════════════════════════════════════════════════════════════════════
type Nivel = 'vermelho' | 'amarelo' | 'verde' | 'info';
type Confianca = 'alta' | 'media' | 'baixa';
interface AudHipotese {
  cenario: number | null; descricao: string; confianca: Confianca;
  boletins: number[]; quarteiroes: string[];
  candidatos?: { codigo: string; boletins: number[] }[];
}
interface AudAchado {
  nivel: Nivel; cenario: number | null; titulo: string; evidencia: string; norte: string;
  confianca: Confianca; boletins: number[]; quarteiroes: string[]; agentes: string[];
  hipoteses: AudHipotese[];
}
interface AudGrupo {
  id: string; quarteiroes: string[]; boletins: number[]; agentes: string[];
  periodo: { inicio: string; fim: string }; informados: number; rg: number; saldo: number;
}
interface AudLupa {
  grupo: string; saldo: number; falta_total: number; sobra_total: number;
  quarteiroes_que_nao_fecham: { codigo: string; imoveis_rg: number; boletins: number[] }[];
  boletins_com_sobra: {
    boletim: number; informados: number; quarteiroes: string[];
    mediana_agente?: number | null; desvio_mediana?: number | null; destaque?: boolean;
  }[];
}
interface Auditoria {
  area: { id: number; nome: string };
  ciclo: { id: number; nome: string; inicio: string; fim: string | null };
  tolerancia_imoveis: number;
  semaforo: 'verde' | 'amarelo' | 'vermelho';
  resumo_texto: string[];
  comece_por_aqui: AudAchado[];
  balanco: {
    boletins: number; informados: number; rg_total: number; saldo: number;
    cobertura_bruta: number | null; cobertura_liquida: number | null;
    fechados: number; recusados: number; recuperados: number;
    por_tipo: Record<string, { campo: number; rg: number; saldo: number }>;
    nota_por_tipo: string;
  };
  quarteiroes: {
    rg_total: number; concluidos: number; tem_lista_de_codigos: boolean;
    nunca_citados: { codigo: string; imoveis_rg: number }[]; imoveis_nunca_citados: number;
    nao_concluidos?: { codigo: string; imoveis_rg: number; citado_em_boletins: number[]; agentes: string[] }[];
    concluido_sem_trabalhado?: { codigo: string; boletins: number[] }[];
    trabalhado_nunca_concluido?: string[];
    fora_do_rg: { codigo: string; boletins: number[]; como: string[]; existe_em_outra_area: string[]; agentes: string[] }[];
    formato_estranho: { boletim: number; campo: string; valor: string; motivo: string[] }[];
  };
  grupos: {
    total: number; lista: AudGrupo[];
    boletins_sem_quarteirao: { boletim: number; data: string; agente: string; quarteirao_texto: string | null; informados: number }[];
  };
  lupa: Record<string, AudLupa>;
  achados: AudAchado[];
  limitacoes: string[];
}

type CorTema = 'error' | 'warning' | 'success' | 'info';
const COR_NIVEL: Record<Nivel, CorTema> = { vermelho: 'error', amarelo: 'warning', verde: 'success', info: 'info' };
const ROTULO_NIVEL: Record<Nivel, string> = { vermelho: 'Vermelho', amarelo: 'Amarelo', verde: 'Verde', info: 'Info' };
const ROTULO_CONF: Record<Confianca, string> = { alta: 'Alta', media: 'Média', baixa: 'Baixa' };
const SEMAFORO: Record<Auditoria['semaforo'], { cor: CorTema; texto: string }> = {
  verde:    { cor: 'success', texto: 'Verde — pode fechar' },
  amarelo:  { cor: 'warning', texto: 'Amarelo — investigar' },
  vermelho: { cor: 'error',   texto: 'Vermelho — há quarteirão sem concluir' },
};
const NOME_TIPO: Record<string, string> = { R: 'Residência', C: 'Comércio', TB: 'Terreno baldio', PE: 'Ponto estratégico', O: 'Outros' };

const fmtDia = (iso?: string | null) => {
  if (!iso) return '—';
  const [, m, d] = iso.split('-');
  return d && m ? `${d}/${m}` : iso;
};
const sinal = (n: number) => (n > 0 ? `+${n}` : `${n}`);
const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

const AUD_PRINT_CSS = `
  .aud-soprint { display: none; }
  @media print {
    @page { size: A4 portrait; margin: 12mm; }
    body { background: #fff !important; }
    .aud-noprint { display: none !important; }
    .aud-soprint { display: block !important; }
    .aud-card { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; }
  }
`;

// Cartão com faixa de título (mesmo desenho dos blocos do Fechamento)
function AudCartao({ titulo, extra, children }: { titulo: string; extra?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card className="aud-card" sx={{ borderRadius: 3, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText', px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.8px', flex: 1 }}>
          {titulo}
        </Typography>
        {extra}
      </Box>
      <CardContent sx={{ py: '12px !important', display: 'flex', flexDirection: 'column', gap: 1.2 }}>
        {children}
      </CardContent>
    </Card>
  );
}

function BotoesBoletim({ ids, onAbrir, limite = 10 }: { ids: number[]; onAbrir?: (id: number) => void; limite?: number }) {
  const [todos, setTodos] = useState(false);
  if (!ids || ids.length === 0) return null;
  const vis = todos ? ids : ids.slice(0, limite);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {vis.map(id => (
        <Button key={id} size="small" variant="outlined" disabled={!onAbrir}
          onClick={() => onAbrir?.(id)}
          sx={{ minWidth: 0, py: 0, px: 0.8, fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', lineHeight: 1.7 }}>
          Boletim #{id}
        </Button>
      ))}
      {ids.length > limite && (
        <Button size="small" className="aud-noprint" onClick={() => setTodos(!todos)}
          sx={{ minWidth: 0, py: 0, px: 0.8, fontSize: '0.7rem', textTransform: 'none' }}>
          {todos ? 'mostrar menos' : `+${ids.length - limite} boletins`}
        </Button>
      )}
    </Box>
  );
}

function ChipsQuarteirao({ codigos, cor }: { codigos: string[]; cor?: CorTema }) {
  if (!codigos || codigos.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {codigos.map(c => (
        <Chip key={c} label={`Q${c}`} size="small" variant="outlined" color={cor ?? 'default'}
          sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
      ))}
    </Box>
  );
}

function AchadoItem({ a, onAbrir }: { a: AudAchado; onAbrir?: (id: number) => void }) {
  const [outras, setOutras] = useState(false);
  const cor = COR_NIVEL[a.nivel];
  const candidatos = (a.hipoteses[0]?.candidatos ?? []).filter(c => c.boletins.length > 0);
  const outrasHip = a.hipoteses.slice(1);
  return (
    <Box className="aud-card" sx={{
      border: 1, borderColor: 'divider', borderLeftWidth: 6, borderLeftColor: `${cor}.main`,
      borderRadius: 1.5, bgcolor: 'background.paper', p: 1.5,
      display: 'flex', flexDirection: 'column', gap: 0.8,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={ROTULO_NIVEL[a.nivel]} color={cor} size="small" sx={{ fontWeight: 800, height: 22 }} />
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', flex: 1, minWidth: 180 }}>{a.titulo}</Typography>
        <Chip label={`Confiança: ${ROTULO_CONF[a.confianca]}`} size="small" variant="outlined"
          color={a.confianca === 'alta' ? 'success' : a.confianca === 'media' ? 'warning' : 'default'}
          sx={{ fontWeight: 700, height: 22, fontSize: '0.68rem' }} />
      </Box>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{a.evidencia}</Typography>
      <Typography sx={{ fontSize: '0.82rem' }}>
        <Box component="span" sx={{ fontWeight: 800 }}>O que fazer: </Box>{a.norte}
      </Typography>
      <ChipsQuarteirao codigos={a.quarteiroes} cor={cor} />
      {candidatos.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Números parecidos citados nos boletins com sobra:</Typography>
          {candidatos.map(c => (
            <Box key={c.codigo} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip label={`Q${c.codigo}`} size="small" sx={{ fontWeight: 800, height: 22 }} />
              <BotoesBoletim ids={c.boletins} onAbrir={onAbrir} limite={6} />
            </Box>
          ))}
        </Box>
      ) : (
        <BotoesBoletim ids={a.boletins} onAbrir={onAbrir} />
      )}
      {outrasHip.length > 0 && (
        <Box>
          <Button size="small" className="aud-noprint" onClick={() => setOutras(!outras)}
            endIcon={outras ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700, px: 0.5 }}>
            Outras hipóteses ({outrasHip.length})
          </Button>
          <Collapse in={outras}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1.5, mt: 0.5, borderLeft: 2, borderColor: 'divider' }}>
              {outrasHip.map((h, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.78rem' }}>
                    <Chip label={ROTULO_CONF[h.confianca]} size="small" variant="outlined" sx={{ mr: 0.8, height: 20, fontSize: '0.65rem' }} />
                    {h.descricao}
                  </Typography>
                  <BotoesBoletim ids={h.boletins} onAbrir={onAbrir} limite={6} />
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

function Numero({ valor, rotulo, cor, nota }: { valor: React.ReactNode; rotulo: string; cor?: string; nota?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: cor ?? 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
        {valor}
      </Typography>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>{rotulo}</Typography>
      {nota && <Typography sx={{ fontSize: '0.68rem', color: cor ?? 'text.secondary', fontWeight: 600 }}>{nota}</Typography>}
    </Box>
  );
}

function PainelAuditoria({ aud, loading, erro, selecionado, onAtualizar, onAbrirFAD01 }: {
  aud: Auditoria | null; loading: boolean; erro: string; selecionado: boolean;
  onAtualizar: () => void; onAbrirFAD01?: (id: number) => void;
}) {
  const [filtro, setFiltro] = useState<'todos' | Nivel>('todos');
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [sobraAberta, setSobraAberta] = useState<Set<string>>(new Set());
  const [verFormatos, setVerFormatos] = useState(false);

  const alternar = (set: Set<string>, fn: (s: Set<string>) => void, k: string) => {
    const n = new Set(set);
    if (n.has(k)) n.delete(k); else n.add(k);
    fn(n);
  };

  const barra = (
    <Box className="aud-noprint" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
      {loading && <CircularProgress size={20} />}
      <Button variant="outlined" size="small" startIcon={<Refresh />} disabled={!selecionado || loading} onClick={onAtualizar}
        sx={{ fontWeight: 700 }}>
        Atualizar auditoria
      </Button>
      <Button variant="contained" size="small" startIcon={<Print />} disabled={!aud} onClick={() => window.print()}
        sx={{ fontWeight: 700 }}>
        Imprimir auditoria
      </Button>
    </Box>
  );

  if (!selecionado) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        <Typography sx={{ fontWeight: 600 }}>Selecione uma área e um ciclo para ver a auditoria</Typography>
      </Box>
    );
  }
  if (!aud) {
    return (
      <>
        {barra}
        {erro && <Alert severity="error">{erro}</Alert>}
        {loading && !erro && (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontWeight: 600, mt: 1 }}>Carregando auditoria…</Typography>
          </Box>
        )}
      </>
    );
  }

  const sem = SEMAFORO[aud.semaforo];
  const b = aud.balanco;
  const q = aud.quarteiroes;
  const tol = aud.tolerancia_imoveis;
  const corSaldo = (s: number) => (s === 0 ? 'text.primary' : Math.abs(s) <= tol ? 'success.main' : 'warning.main');
  const contagem = (n: Nivel) => aud.achados.filter(a => a.nivel === n).length;
  const achadosFiltrados = filtro === 'todos' ? aud.achados : aud.achados.filter(a => a.nivel === filtro);
  const tipos = ['R', 'C', 'TB', 'PE', 'O'].filter(t => b.por_tipo[t]);
  const somaTipo = (k: 'campo' | 'rg' | 'saldo') => tipos.reduce((s, t) => s + b.por_tipo[t][k], 0);
  const cel = { fontSize: '0.8rem', py: 0.6, px: 1, fontVariantNumeric: 'tabular-nums' } as const;
  const celH = { ...cel, fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase' as const, color: 'text.secondary' };

  return (
    <>
      <GlobalStyles styles={AUD_PRINT_CSS} />
      {barra}
      {erro && <Alert severity="error" className="aud-noprint">{erro}</Alert>}

      {/* Título só na impressão */}
      <Box className="aud-soprint">
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Auditoria de Fechamento — {aud.area.nome} · {aud.ciclo.nome}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          TOVIS · emitido em {new Date().toLocaleString('pt-BR')}
        </Typography>
      </Box>

      {/* 1. Semáforo + resumo */}
      <Card className="aud-card" sx={{ borderRadius: 3, overflow: 'hidden', border: 2, borderColor: `${sem.cor}.main` }}>
        <Alert severity={sem.cor} variant="filled" sx={{ borderRadius: 0, alignItems: 'center', '& .MuiAlert-icon': { fontSize: 32 } }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.1rem', md: '1.35rem' } }}>{sem.texto}</Typography>
          <Typography sx={{ fontSize: '0.78rem', opacity: 0.95 }}>{aud.area.nome} · {aud.ciclo.nome}</Typography>
        </Alert>
        <CardContent sx={{ py: '12px !important' }}>
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {aud.resumo_texto.map((t, i) => (
              <li key={i}><Typography sx={{ fontSize: '0.84rem', fontWeight: i === aud.resumo_texto.length - 1 && t.startsWith('Comece') ? 800 : 400 }}>{t}</Typography></li>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* 2. Comece por aqui */}
      <AudCartao titulo="Comece por aqui">
        {aud.comece_por_aqui.length === 0 ? (
          <Alert severity="success">Nada para investigar: nenhum achado vermelho, amarelo ou verde.</Alert>
        ) : aud.comece_por_aqui.map((a, i) => <AchadoItem key={i} a={a} onAbrir={onAbrirFAD01} />)}
      </AudCartao>

      {/* 3. Balanço */}
      <AudCartao titulo="Balanço da área">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 2 }}>
          <Numero valor={b.informados.toLocaleString('pt-BR')} rotulo="Informados (boletins)" />
          <Numero valor={b.rg_total.toLocaleString('pt-BR')} rotulo="Imóveis no RG" />
          <Numero valor={sinal(b.saldo)} rotulo="Saldo" cor={corSaldo(b.saldo)}
            nota={Math.abs(b.saldo) <= tol ? `dentro da tolerância de ${tol}` : 'acima da tolerância'} />
          <Numero valor={b.cobertura_bruta != null ? `${b.cobertura_bruta}%` : '—'} rotulo="Cobertura bruta" />
          <Numero valor={b.cobertura_liquida != null ? `${b.cobertura_liquida}%` : '—'} rotulo="Cobertura líquida" />
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          Bruta = informados ÷ RG. Líquida = só os trabalhados ÷ RG. {plural(b.boletins, 'boletim', 'boletins')} ·
          fechados {b.fechados} · recusados {b.recusados} · recuperados {b.recuperados}.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 360 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={celH}>Tipo</TableCell>
                <TableCell sx={celH} align="right">Campo</TableCell>
                <TableCell sx={celH} align="right">RG</TableCell>
                <TableCell sx={celH} align="right">Saldo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tipos.map(t => (
                <TableRow key={t}>
                  <TableCell sx={{ ...cel, fontWeight: 700 }}>{t} · {NOME_TIPO[t]}</TableCell>
                  <TableCell sx={cel} align="right">{b.por_tipo[t].campo.toLocaleString('pt-BR')}</TableCell>
                  <TableCell sx={cel} align="right">{b.por_tipo[t].rg.toLocaleString('pt-BR')}</TableCell>
                  <TableCell sx={{ ...cel, fontWeight: 800 }} align="right">{sinal(b.por_tipo[t].saldo)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ ...cel, fontWeight: 800 }}>Trabalhados</TableCell>
                <TableCell sx={{ ...cel, fontWeight: 800 }} align="right">{somaTipo('campo').toLocaleString('pt-BR')}</TableCell>
                <TableCell sx={{ ...cel, fontWeight: 800 }} align="right">{somaTipo('rg').toLocaleString('pt-BR')}</TableCell>
                <TableCell sx={{ ...cel, fontWeight: 800 }} align="right">{sinal(somaTipo('saldo'))}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          <Box component="span" sx={{ fontWeight: 800 }}>Nota: </Box>{b.nota_por_tipo}.
        </Typography>
      </AudCartao>

      {/* 4. Quarteirões */}
      <AudCartao titulo="Quarteirões">
        <Numero valor={`${q.concluidos} de ${q.rg_total}`} rotulo="Quarteirões concluídos"
          cor={q.concluidos >= q.rg_total ? 'success.main' : 'error.main'} />
        {!q.tem_lista_de_codigos && (
          <Alert severity="info">
            Nenhum boletim desta área e ciclo tem a <b>lista de códigos</b> dos quarteirões concluídos.
            A conclusão foi conferida só pelo número total — não dá para saber quais faltam.
          </Alert>
        )}
        {q.nao_concluidos && q.nao_concluidos.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'error.main' }}>
              Não concluídos ({q.nao_concluidos.length})
            </Typography>
            {q.nao_concluidos.map(n => (
              <Box key={n.codigo} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', pl: 1, borderLeft: 3, borderColor: 'error.main' }}>
                <Chip label={`Q${n.codigo}`} color="error" size="small" sx={{ fontWeight: 800, height: 22 }} />
                <Typography sx={{ fontSize: '0.78rem' }}>{plural(n.imoveis_rg, 'imóvel', 'imóveis')} no RG</Typography>
                {n.citado_em_boletins.length === 0 ? (
                  <Chip label="nunca citado" color="error" variant="outlined" size="small" sx={{ fontWeight: 800, height: 22 }} />
                ) : (
                  <>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>citado em:</Typography>
                    <BotoesBoletim ids={n.citado_em_boletins} onAbrir={onAbrirFAD01} limite={6} />
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}
        {!q.tem_lista_de_codigos && q.nunca_citados.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'error.main' }}>
              Nunca citados ({q.nunca_citados.length}) — {plural(q.imoveis_nunca_citados, 'imóvel', 'imóveis')}
            </Typography>
            <ChipsQuarteirao codigos={q.nunca_citados.map(n => n.codigo)} cor="error" />
          </Box>
        )}
        {q.fora_do_rg.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'warning.main' }}>Fora do RG ({q.fora_do_rg.length})</Typography>
            {q.fora_do_rg.map(f => (
              <Box key={f.codigo} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', pl: 1, borderLeft: 3, borderColor: 'warning.main' }}>
                <Chip label={`Q${f.codigo}`} color="warning" size="small" sx={{ fontWeight: 800, height: 22 }} />
                <Typography sx={{ fontSize: '0.75rem' }}>
                  como {f.como.join(' e ')}{f.existe_em_outra_area.length > 0 ? ` · existe no RG de ${f.existe_em_outra_area.join(', ')}` : ''}
                </Typography>
                <BotoesBoletim ids={f.boletins} onAbrir={onAbrirFAD01} limite={6} />
              </Box>
            ))}
          </Box>
        )}
        {q.concluido_sem_trabalhado && q.concluido_sem_trabalhado.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'warning.main' }}>
              Concluído sem aparecer como trabalhado ({q.concluido_sem_trabalhado.length})
            </Typography>
            {q.concluido_sem_trabalhado.map(c => (
              <Box key={c.codigo} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', pl: 1, borderLeft: 3, borderColor: 'warning.main' }}>
                <Chip label={`Q${c.codigo}`} color="warning" variant="outlined" size="small" sx={{ fontWeight: 800, height: 22 }} />
                <BotoesBoletim ids={c.boletins} onAbrir={onAbrirFAD01} limite={6} />
              </Box>
            ))}
          </Box>
        )}
        {q.trabalhado_nunca_concluido && q.trabalhado_nunca_concluido.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem' }}>
              Trabalhados e nunca concluídos ({q.trabalhado_nunca_concluido.length})
            </Typography>
            <ChipsQuarteirao codigos={q.trabalhado_nunca_concluido} cor="warning" />
          </Box>
        )}
        {q.formato_estranho.length > 0 && (
          <Box>
            <Button size="small" onClick={() => setVerFormatos(!verFormatos)}
              endIcon={verFormatos ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 700, px: 0.5 }}>
              Códigos em formato diferente ({q.formato_estranho.length})
            </Button>
            <Collapse in={verFormatos}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {q.formato_estranho.map((e, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <BotoesBoletim ids={[e.boletim]} onAbrir={onAbrirFAD01} />
                    <Typography sx={{ fontSize: '0.75rem' }}>
                      <b>“{e.valor}”</b> em {e.campo} — {e.motivo.join(', ')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </AudCartao>

      {/* 5. Grupos */}
      <AudCartao titulo={`Grupos de quarteirões (${aud.grupos.total})`}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          <b>Grupo</b> = quarteirões ligados por boletins que os citam juntos. Clique na linha para abrir a lupa.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...celH, width: 32 }} />
                <TableCell sx={celH}>Grupo</TableCell>
                <TableCell sx={celH} align="right">Qrts</TableCell>
                <TableCell sx={celH} align="right">Boletins</TableCell>
                <TableCell sx={celH}>Agentes</TableCell>
                <TableCell sx={celH}>Período</TableCell>
                <TableCell sx={celH} align="right">Informados</TableCell>
                <TableCell sx={celH} align="right">RG</TableCell>
                <TableCell sx={celH} align="right">Saldo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aud.grupos.lista.map(g => {
                const lp = aud.lupa[g.id];
                const aberto = abertos.has(g.id);
                const sobraOn = sobraAberta.has(g.id);
                return (
                  <React.Fragment key={g.id}>
                    <TableRow hover={!!lp} onClick={lp ? () => alternar(abertos, setAbertos, g.id) : undefined}
                      sx={{ cursor: lp ? 'pointer' : 'default', '& > td': { borderBottom: aberto ? 0 : undefined } }}>
                      <TableCell sx={cel}>
                        {lp && <IconButton size="small" className="aud-noprint" aria-label="abrir lupa">
                          {aberto ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </IconButton>}
                      </TableCell>
                      <TableCell sx={{ ...cel, fontWeight: 800 }}>
                        <Tooltip title={`Quarteirões: ${g.quarteiroes.join(', ')}`}><span>{g.id}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={cel} align="right">{g.quarteiroes.length}</TableCell>
                      <TableCell sx={cel} align="right">{g.boletins.length}</TableCell>
                      <TableCell sx={{ ...cel, fontSize: '0.72rem' }}>
                        {g.agentes.slice(0, 2).join(', ')}{g.agentes.length > 2 ? ` +${g.agentes.length - 2}` : ''}
                      </TableCell>
                      <TableCell sx={{ ...cel, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{fmtDia(g.periodo.inicio)} → {fmtDia(g.periodo.fim)}</TableCell>
                      <TableCell sx={cel} align="right">{g.informados.toLocaleString('pt-BR')}</TableCell>
                      <TableCell sx={cel} align="right">{g.rg.toLocaleString('pt-BR')}</TableCell>
                      <TableCell sx={{ ...cel, fontWeight: 900, color: corSaldo(g.saldo) }} align="right">{sinal(g.saldo)}</TableCell>
                    </TableRow>
                    {lp && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, px: 1 }}>
                          <Collapse in={aberto} unmountOnExit>
                            <Box sx={{ py: 1.2, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                Quarteirões do grupo: {g.quarteiroes.join(', ')}
                              </Typography>
                              {/* Onde FALTA (em destaque) */}
                              <Box sx={{ border: 1, borderColor: 'error.main', borderLeftWidth: 6, borderRadius: 1.5, p: 1.2 }}>
                                <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: 'error.main', mb: 0.5 }}>
                                  Onde FALTA{lp.falta_total > 0 ? ` — ${plural(lp.falta_total, 'imóvel', 'imóveis')}` : ''}
                                </Typography>
                                {lp.quarteiroes_que_nao_fecham.length === 0 ? (
                                  <Typography sx={{ fontSize: '0.78rem' }}>Nenhum quarteirão sem cobertura neste grupo.</Typography>
                                ) : (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                    {lp.quarteiroes_que_nao_fecham.map(f => (
                                      <Box key={f.codigo} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Chip label={`Q${f.codigo}`} color="error" size="small" sx={{ fontWeight: 800, height: 22 }} />
                                        <Typography sx={{ fontSize: '0.75rem' }}>{plural(f.imoveis_rg, 'imóvel', 'imóveis')} no RG</Typography>
                                        <BotoesBoletim ids={f.boletins} onAbrir={onAbrirFAD01} limite={5} />
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                              {/* Onde SOBRA (recolhido) */}
                              {lp.boletins_com_sobra.length > 0 && (
                                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1 }}>
                                  <Button size="small" onClick={() => alternar(sobraAberta, setSobraAberta, g.id)}
                                    endIcon={sobraOn ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    sx={{ textTransform: 'none', fontWeight: 800, fontSize: '0.8rem', px: 0.5 }}>
                                    Onde SOBRA — {plural(lp.sobra_total, 'imóvel', 'imóveis')} em {plural(lp.boletins_com_sobra.length, 'boletim', 'boletins')}
                                  </Button>
                                  <Collapse in={sobraOn} unmountOnExit>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', my: 0.5 }}>
                                      Primeiro os boletins mais acima da mediana do próprio agente.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      {lp.boletins_com_sobra.map(x => (
                                        <Box key={x.boletim} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap',
                                          pl: 1, borderLeft: 3, borderColor: x.destaque ? 'warning.main' : 'transparent' }}>
                                          <BotoesBoletim ids={[x.boletim]} onAbrir={onAbrirFAD01} />
                                          {x.destaque && <Chip label="destaque" color="warning" size="small" sx={{ fontWeight: 800, height: 20, fontSize: '0.65rem' }} />}
                                          <Typography sx={{ fontSize: '0.75rem', fontWeight: x.destaque ? 700 : 400 }}>
                                            informa {x.informados}
                                            {x.mediana_agente != null && x.desvio_mediana != null
                                              ? ` · mediana do agente ${x.mediana_agente} (${sinal(x.desvio_mediana)})` : ''}
                                            {x.quarteiroes.length > 0 ? ` · Q ${x.quarteiroes.join(', ')}` : ''}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Collapse>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {aud.grupos.boletins_sem_quarteirao.length > 0 && (
          <Alert severity="warning">
            <b>{plural(aud.grupos.boletins_sem_quarteirao.length, 'boletim', 'boletins')} sem quarteirão legível</b> (fora dos grupos):
            <Box sx={{ mt: 0.5 }}>
              <BotoesBoletim ids={aud.grupos.boletins_sem_quarteirao.map(x => x.boletim)} onAbrir={onAbrirFAD01} />
            </Box>
          </Alert>
        )}
      </AudCartao>

      {/* 6. Todos os achados */}
      <AudCartao titulo={`Todos os achados (${aud.achados.length})`}>
        <Box className="aud-noprint" sx={{ overflowX: 'auto' }}>
          <ToggleButtonGroup size="small" exclusive value={filtro} onChange={(_, v) => v && setFiltro(v)}>
            <ToggleButton value="todos" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Todos ({aud.achados.length})</ToggleButton>
            {(['vermelho', 'amarelo', 'verde', 'info'] as Nivel[]).map(n => (
              <ToggleButton key={n} value={n} color={COR_NIVEL[n]} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
                {ROTULO_NIVEL[n]} ({contagem(n)})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {achadosFiltrados.length === 0
          ? <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Nenhum achado neste nível.</Typography>
          : achadosFiltrados.map((a, i) => <AchadoItem key={`${filtro}-${i}`} a={a} onAbrir={onAbrirFAD01} />)}
      </AudCartao>

      {/* 7. Limitações */}
      <Box className="aud-card" sx={{ px: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase' }}>Limitações</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {aud.limitacoes.map((l, i) => (
            <li key={i}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{l}</Typography></li>
          ))}
        </Box>
      </Box>
    </>
  );
}
